import { Badge, Stack, Text, Title } from "@mantine/core";
import type { DataTableColumn, DataTableSortStatus } from "mantine-datatable";
import { DataTable } from "mantine-datatable";
import { useEffect, useMemo, useState } from "react";
import { client } from "@/api-client";
import type {
  AdminTrainerBasicOutput,
  AdminTrainerDetailOutput,
} from "@/api-types";
import { AccountDetails } from "../components/AccountDetails";

const Trainers = () => {
  const { data: trainersData, isLoading } = client.useQuery(
    "get",
    "/admin/trainers",
  );
  const [selectedTrainer, setSelectedTrainer] =
    useState<AdminTrainerBasicOutput | null>(null);
  const [selectedTrainerDetails, setSelectedTrainerDetails] =
    useState<AdminTrainerDetailOutput | null>(null);

  // Query for detailed trainer information when a trainer is selected
  const { data: trainerDetailsData } = client.useQuery(
    "get",
    "/admin/trainers/{trainer_id}",
    {
      params: { path: { trainer_id: selectedTrainer?.id || "" } },
    },
    {
      enabled: !!selectedTrainer?.id,
    },
  );

  // Update detailed trainer data when query completes
  useEffect(() => {
    if (trainerDetailsData) {
      setSelectedTrainerDetails(trainerDetailsData);
    }
  }, [trainerDetailsData]);

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<AdminTrainerBasicOutput>
  >({
    columnAccessor: "name",
    direction: "asc",
  });

  const sortedRecords = useMemo(() => {
    if (!trainersData?.trainers) return [];

    return [...trainersData.trainers].sort((a, b) => {
      const aVal =
        a[sortStatus.columnAccessor as keyof AdminTrainerBasicOutput];
      const bVal =
        b[sortStatus.columnAccessor as keyof AdminTrainerBasicOutput];

      if (sortStatus.columnAccessor === "name") {
        const aName = `${a.first_name} ${a.last_name}`;
        const bName = `${b.first_name} ${b.last_name}`;
        return sortStatus.direction === "desc"
          ? bName.localeCompare(aName)
          : aName.localeCompare(bName);
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortStatus.direction === "desc"
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      }

      return 0;
    });
  }, [trainersData, sortStatus]);

  const columns = useMemo<DataTableColumn<AdminTrainerBasicOutput>[]>(
    () => [
      {
        accessor: "name",
        title: "Name",
        sortable: true,
        render: (trainer) => `${trainer.first_name} ${trainer.last_name}`,
      },
      {
        accessor: "email",
        title: "Email",
        sortable: true,
      },
      {
        accessor: "locality",
        title: "Location",
        sortable: true,
      },
      {
        accessor: "country",
        title: "Country",
        sortable: true,
      },
      {
        accessor: "created_at",
        title: "Created",
        render: (trainer) => new Date(trainer.created_at).toLocaleDateString(),
        sortable: true,
      },
      {
        accessor: "status",
        title: "Status",
        render: (trainer) => (
          <Badge
            color={trainer.status === "NORMAL" ? "green" : "red"}
            variant="light"
          >
            {trainer.status}
          </Badge>
        ),
        sortable: true,
      },
    ],
    [],
  );

  if (isLoading) {
    return <Text>Loading trainers...</Text>;
  }

  if (!trainersData) {
    return <Text>No trainers data available.</Text>;
  }

  return (
    <Stack gap="xl" h="100%">
      <Title order={1}>Trainers ({trainersData.trainers.length})</Title>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 200px)",
        }}
      >
        {/* Table Section */}
        <div
          style={{
            flex: selectedTrainer ? "0 0 50%" : "1",
            overflow: "auto",
            paddingBottom: "1rem",
          }}
        >
          <DataTable
            columns={columns}
            records={sortedRecords}
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            minHeight={180}
            noRecordsText="No trainers found"
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            onRowClick={({ record }) => {
              setSelectedTrainer(record);
              setSelectedTrainerDetails(null); // Clear previous details while loading new ones
            }}
            selectedRecords={selectedTrainer ? [selectedTrainer] : []}
          />
        </div>

        {/* Details Panel */}
        {selectedTrainer && (
          <div
            style={{
              flex: "0 0 50%",
              overflow: "auto",
              paddingTop: "1rem",
              borderTop: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {selectedTrainerDetails ? (
              <AccountDetails
                account={selectedTrainerDetails}
                accountType="trainer"
              />
            ) : (
              <Text>Loading trainer details...</Text>
            )}
          </div>
        )}
      </div>
    </Stack>
  );
};

export default Trainers;
